import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Mail, CheckCircle, RefreshCw } from 'lucide-react';
import { APP_CONFIG } from '@/config/app-config';

interface EmailVerificationWaitingProps {
  email: string;
  userType: 'client' | 'practitioner';
  onBackToLogin: () => void;
}

export default function EmailVerificationWaiting({
  email,
  userType,
  onBackToLogin
}: EmailVerificationWaitingProps) {
  const { t } = useTranslation(userType === 'client' ? 'client-auth' : 'practitioner-auth');
  const { toast } = useToast();
  const [isResending, setIsResending] = React.useState(false);
  const [resendCount, setResendCount] = React.useState(0);
  const [lastResendTime, setLastResendTime] = React.useState<number>(0);

  const handleResendEmail = async () => {
    if (resendCount >= 3) {
      toast({
        variant: "destructive",
        title: t('limitReached'),
        description: t('limitReachedMessage'),
      });
      return;
    }

    // Check if enough time has passed since last resend (minimum 60 seconds)
    const now = Date.now();
    const timeSinceLastResend = now - lastResendTime;
    const minInterval = 60000; // 60 seconds

    if (timeSinceLastResend < minInterval && lastResendTime > 0) {
      const remainingTime = Math.ceil((minInterval - timeSinceLastResend) / 1000);
      toast({
        variant: "destructive",
        title: "请等待",
        description: `请等待 ${remainingTime} 秒后再重新发送邮件`,
      });
      return;
    }

    setIsResending(true);
    try {
      const redirectPath = userType === 'practitioner' ? '/practitioner-auth' : '/client-auth';
      const redirectTo = `${window.location.origin}${redirectPath}`;

      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: email,
        options: {
          emailRedirectTo: redirectTo
        }
      });

      if (error) {
        throw error;
      }

      setResendCount(prev => prev + 1);
      setLastResendTime(now);
      toast({
        title: t('emailResent'),
        description: t('emailResentMessage'),
      });
    } catch (error: any) {
      let errorMessage = error.message || t('resendFailedMessage');
      
      // Handle rate limit errors specifically
      if (error.message?.includes('rate') || error.message?.includes('limit') || error.message?.includes('too many')) {
        errorMessage = "邮件发送频率过高，请稍后再试。Supabase免费版每小时限制30封邮件。";
      }
      
      toast({
        variant: "destructive",
        title: t('resendFailed'),
        description: errorMessage,
      });
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-amber-50 animate-fade-in-up">
      <div className="flex min-h-screen items-center justify-center px-4 py-8">
        <div className="w-full max-w-md animate-scale-fade-in">
          <div className="rounded-xl border border-neutral-200 bg-white p-8 shadow-sm text-center">
            {/* Success Icon */}
            <div className="flex justify-center mb-6">
              <div className="relative">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <div className="absolute -top-1 -right-1 w-6 h-6 bg-amber-100 rounded-full flex items-center justify-center">
                  <Mail className="w-3 h-3 text-amber-600" />
                </div>
              </div>
            </div>

            {/* Title */}
            <h1 className="text-2xl font-semibold text-neutral-800 mb-2">
              {t('registrationSuccessful')}
            </h1>

            {/* Description */}
            <p className="text-neutral-600 mb-6">
              {t('checkEmailVerify')}
            </p>

            {/* Email Display */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-neutral-600 mb-1">
                {t('emailSentTo')}
              </p>
              <p className="font-medium text-neutral-800 break-all">
                {email}
              </p>
            </div>

            {/* Instructions */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 text-left">
              <h3 className="font-medium text-blue-800 mb-2">
                📧 {t('nextSteps')}
              </h3>
              <ol className="text-sm text-blue-700 space-y-1">
                <li>{t('checkInbox')}</li>
                <li>{t('checkSpam')}</li>
                <li>{t('clickVerifyLink')}</li>
                <li>{t('returnToLogin')}</li>
              </ol>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
              {/* Resend Email Button */}
              <Button
                onClick={handleResendEmail}
                disabled={isResending || resendCount >= 3}
                variant="outline"
                className="w-full border-amber-300 text-amber-700 hover:bg-amber-50"
              >
                {isResending ? (
                  <>
                    <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                    {t('sending')}
                  </>
                ) : (
                  <>
                    <Mail className="mr-2 h-4 w-4" />
                    {t('resendEmail')} {resendCount > 0 && `(${resendCount}/3)`}
                  </>
                )}
              </Button>

              {/* Back to Login Button */}
              <Button
                onClick={onBackToLogin}
                variant="ghost"
                className="w-full text-neutral-600 hover:text-neutral-800"
              >
                {t('backToLogin')}
              </Button>
            </div>

            {/* Help Text */}
            <p className="text-xs text-neutral-500 mt-6">
              {t('helpText')}
              <br />
              {t('contactSupport')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}